import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Bell, CheckCircle, XCircle, AlertTriangle, Wifi, Key, Globe, Copy, RefreshCw, Code, Settings as SettingsIcon, Info, Clock, Zap, Share2, QrCode, ExternalLink, Smartphone, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { getPortalBaseUrl } from '../utils/portal_url';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('reminder');
  
  // Reminder Settings
  const [reminderSettings, setReminderSettings] = useState({
    enabled: true,
    hours: 3
  });

  // PPPoE Webhook Settings
  const [webhookSettings, setWebhookSettings] = useState({
    api_key: '',
    custom_webhook_url: '',
    ip_whitelist_enabled: false,
    allowed_ips: [],
    scheduler_enabled: true,
    scheduler_interval_minutes: 5
  });
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [pushAllLoading, setPushAllLoading] = useState(false);
  const [pushHotspotLoading, setPushHotspotLoading] = useState(false);
  const [scriptTemplate, setScriptTemplate] = useState(null);
  const [newAllowedIp, setNewAllowedIp] = useState('');
  const [portalLinkCopied, setPortalLinkCopied] = useState(false);
  const [tenantSlug, setTenantSlug] = useState(null);
  
  // RADIUS Accounting Sync Settings
  const [radiusSyncSettings, setRadiusSyncSettings] = useState({
    enabled: false,
    interval: 2
  });
  const [radiusSyncLoading, setRadiusSyncLoading] = useState(false);
  const [manualSyncLoading, setManualSyncLoading] = useState(false);
  // Billing settings (per-tenant): extend_method + jendela penerbitan tagihan H-N
  const [billingSettings, setBillingSettings] = useState({ extend_method: 'from_due_date', invoice_pregen_days: 5, postpaid_due_anchor: 'cycle_day' });
  const [savingBilling, setSavingBilling] = useState(false);

  const fetchBillingSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/v2/billing-settings`, { headers: { 'Authorization': `Bearer ${token}` } });
      const json = await res.json();
      if (json.success && json.data) {
        setBillingSettings({
          extend_method: json.data.extend_method || 'from_due_date',
          invoice_pregen_days: json.data.invoice_pregen_days ?? 5,
          postpaid_due_anchor: json.data.postpaid_due_anchor || 'cycle_day'
        });
      }
    } catch (e) { console.error('billing-settings fetch:', e); }
  };

  const saveBillingSettings = async () => {
    setSavingBilling(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/v2/billing-settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(billingSettings)
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Pengaturan billing berhasil disimpan');
      } else {
        toast.error(json.detail || 'Gagal menyimpan pengaturan billing');
      }
    } catch (e) {
      toast.error('Gagal menyimpan pengaturan billing');
    } finally {
      setSavingBilling(false);
    }
  };

  useEffect(() => { fetchBillingSettings(); }, []);

  // Portal URLs
  const portalLoginUrl = tenantSlug ? `${getPortalBaseUrl()}/p/${tenantSlug}/login` : '';
  const portalInfoUrl = tenantSlug ? `${getPortalBaseUrl()}/p/${tenantSlug}/portal-info` : '';

  useEffect(() => {
    fetchSettings();
    fetchWebhookSettings();
    fetchTenantSlug();
    fetchRadiusSyncSettings();
  }, []);
  
  // Fetch RADIUS Accounting Sync Settings
  const fetchRadiusSyncSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pppoe/radius-accounting-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.data) {
        setRadiusSyncSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching radius sync settings:', error);
    }
  };
  
  // Save RADIUS Accounting Sync Settings
  const saveRadiusSyncSettings = async () => {
    setRadiusSyncLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pppoe/radius-accounting-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(radiusSyncSettings)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Pengaturan RADIUS Sync berhasil disimpan');
      } else {
        toast.error(data.message || 'Gagal menyimpan pengaturan');
      }
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setRadiusSyncLoading(false);
    }
  };
  
  // Manual Sync
  const runManualSync = async () => {
    setManualSyncLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pppoe/radius-accounting-sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Sync berhasil! ${data.online_count} pelanggan online.`);
      } else {
        toast.error(data.message || 'Gagal sync');
      }
    } catch (error) {
      toast.error('Gagal melakukan sync');
    } finally {
      setManualSyncLoading(false);
    }
  };

  const fetchTenantSlug = async () => {
    // First try localStorage for tenant_slug
    const storedSlug = localStorage.getItem('tenant_slug');
    if (storedSlug) {
      setTenantSlug(storedSlug);
      return;
    }
    
    // Fetch from API
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data?.data?.tenant_slug) {
        setTenantSlug(response.data.data.tenant_slug);
        localStorage.setItem('tenant_slug', response.data.data.tenant_slug);
      }
    } catch (error) {
      console.error('Error fetching tenant info:', error);
    }
  };

 

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/settings/ticket_reminder`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.data) {
        setReminderSettings({
          enabled: response.data.data.enabled ?? true,
          hours: response.data.data.hours ?? 3
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhookSettings = async () => {
    try {
      setWebhookLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/mikrotik/pppoe-webhook-settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setWebhookSettings({
          api_key: data.api_key || '',
          custom_webhook_url: data.custom_webhook_url || '',
          ip_whitelist_enabled: data.ip_whitelist_enabled || false,
          allowed_ips: data.allowed_ips || [],
          scheduler_enabled: data.scheduler_enabled !== false,
          scheduler_interval_minutes: data.scheduler_interval_minutes || 5
        });
        
        // Fetch script template if API key exists
        if (data.api_key) {
          fetchScriptTemplate();
        }
      }
    } catch (error) {
      console.error('Error fetching webhook settings:', error);
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/settings/ticket_reminder`,
        reminderSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'Pengaturan berhasil disimpan!'
        });
        
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Gagal menyimpan pengaturan'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestReminder = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/tickets/reminders/check`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const summary = response.data.summary || {};
        setMessage({
          type: 'info',
          text: `Test selesai: ${summary.tickets_checked || 0} tiket dicek, ${summary.reminders_sent || 0} reminder dikirim`
        });
        
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      console.error('Error testing reminder:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Gagal menjalankan test reminder'
      });
    } finally {
      setSaving(false);
    }
  };

  // Webhook Functions
  const generateApiKey = async () => {
    try {
      setWebhookLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/mikrotik/pppoe-webhook-settings/generate-key`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setWebhookSettings(prev => ({ ...prev, api_key: response.data.api_key }));
        toast.success('API Key baru berhasil di-generate!');
        fetchScriptTemplate();
      }
    } catch (error) {
      toast.error('Gagal generate API Key');
    } finally {
      setWebhookLoading(false);
    }
  };

  const saveWebhookSettings = async () => {
    try {
      setWebhookLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/mikrotik/pppoe-webhook-settings`,
        webhookSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Pengaturan webhook berhasil disimpan!');
        fetchScriptTemplate();
      }
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan webhook');
    } finally {
      setWebhookLoading(false);
    }
  };

  const pushWebhookToAllRouters = async () => {
    try {
      setPushAllLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/mikrotik/push-webhook-all-routers`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const { total_routers, success_count, failed_count } = response.data;
        if (failed_count === 0) {
          toast.success(`✅ Webhook berhasil di-push ke ${success_count} router!`);
        } else {
          toast.warning(`Webhook di-push: ${success_count} sukses, ${failed_count} gagal`);
        }
      } else {
        toast.error(response.data.message || 'Gagal push webhook');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal push webhook ke router');
    } finally {
      setPushAllLoading(false);
    }
  };

  const pushHotspotWebhookToAllRouters = async () => {
    try {
      setPushHotspotLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/mikrotik/push-hotspot-webhook-all-routers`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const { total_routers, success_count, failed_count } = response.data;
        if (failed_count === 0) {
          toast.success(`✅ Hotspot webhook berhasil di-push ke ${success_count} router!`);
        } else {
          toast.warning(`Hotspot webhook di-push: ${success_count} sukses, ${failed_count} gagal`);
        }
      } else {
        toast.error(response.data.message || 'Gagal push hotspot webhook');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal push hotspot webhook ke router');
    } finally {
      setPushHotspotLoading(false);
    }
  };

  const fetchScriptTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/mikrotik/mikrotik-script-template`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setScriptTemplate(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching script template:', error);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} berhasil di-copy!`);
      } else {
        // Fallback for HTTP or older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          toast.success(`${label} berhasil di-copy!`);
        } else {
          toast.error('Gagal copy, silakan copy manual');
        }
      }
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error('Gagal copy, silakan copy manual');
    }
  };

  const addAllowedIp = () => {
    if (newAllowedIp && !webhookSettings.allowed_ips.includes(newAllowedIp)) {
      setWebhookSettings(prev => ({
        ...prev,
        allowed_ips: [...prev.allowed_ips, newAllowedIp]
      }));
      setNewAllowedIp('');
    }
  };

  const removeAllowedIp = (ip) => {
    setWebhookSettings(prev => ({
      ...prev,
      allowed_ips: prev.allowed_ips.filter(i => i !== ip)
    }));
  };

  useEffect(() => {
    if (webhookSettings.api_key) {
      fetchScriptTemplate();
    }
  }, [webhookSettings.api_key]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
        <div className="text-lg">Memuat pengaturan...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
          <SettingsIcon className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Pengaturan Sistem</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Kelola pengaturan otomatisasi dan integrasi MikroTik</p>
        </div>
      </div>

      {message.text && (
        <Alert className={`mb-4 ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border-green-200' :
          message.type === 'error' ? 'bg-red-50 text-red-800 border-red-200' :
          'bg-blue-50 text-blue-800 border-blue-200'
        }`}>
          {message.type === 'success' && <CheckCircle className="h-4 w-4" />}
          {message.type === 'error' && <XCircle className="h-4 w-4" />}
          {message.type === 'info' && <AlertTriangle className="h-4 w-4" />}
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 dark:bg-gray-800 p-1 rounded-xl mb-6">
          <TabsTrigger 
            value="reminder" 
            className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-sm rounded-lg"
          >
            <Bell className="w-4 h-4" />
            Reminder Tiket
          </TabsTrigger>
          <TabsTrigger 
            value="webhook" 
            className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-sm rounded-lg"
          >
            <Wifi className="w-4 h-4" />
            PPPoE Webhook
          </TabsTrigger>
          <TabsTrigger 
            value="portal" 
            className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-sm rounded-lg"
          >
            <Share2 className="w-4 h-4" />
            Portal Pelanggan
          </TabsTrigger>
          <TabsTrigger 
            value="billing" 
            className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-sm rounded-lg"
          >
            <CreditCard className="w-4 h-4" />
            Billing
          </TabsTrigger>
        </TabsList>

        {/* Reminder Tiket Tab */}
        <TabsContent value="reminder">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-indigo-500" />
                <CardTitle>Reminder Tiket Otomatis</CardTitle>
              </div>
              <CardDescription>
                Sistem akan mengirim reminder otomatis via WhatsApp untuk tiket yang belum dikerjakan
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="reminder-enabled" className="text-base">
                    Aktifkan Reminder Otomatis
                  </Label>
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    Kirim notifikasi WhatsApp untuk tiket yang terlalu lama terbuka
                  </p>
                </div>
                <Switch
                  id="reminder-enabled"
                  checked={reminderSettings.enabled}
                  onCheckedChange={(checked) =>
                    setReminderSettings(prev => ({ ...prev, enabled: checked }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reminder-hours">
                  Durasi Sebelum Reminder
                </Label>
                <p className="text-sm text-gray-500 mb-2 dark:text-slate-400">
                  Berapa jam setelah tiket dibuat sebelum mengirim reminder
                </p>
                <Select
                  value={reminderSettings.hours.toString()}
                  onValueChange={(value) =>
                    setReminderSettings(prev => ({ ...prev, hours: parseInt(value) }))
                  }
                  disabled={!reminderSettings.enabled}
                >
                  <SelectTrigger id="reminder-hours" className="w-full md:w-64">
                    <SelectValue placeholder="Pilih durasi" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Jam</SelectItem>
                    <SelectItem value="2">2 Jam</SelectItem>
                    <SelectItem value="3">3 Jam</SelectItem>
                    <SelectItem value="4">4 Jam</SelectItem>
                    <SelectItem value="6">6 Jam</SelectItem>
                    <SelectItem value="12">12 Jam</SelectItem>
                    <SelectItem value="24">24 Jam</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
                <p className="text-sm text-blue-800">
                  <strong>Cara Kerja:</strong>
                  <br />
                  • Sistem akan mengecek tiket setiap 30 menit
                  <br />
                  • Reminder dikirim ke teknisi yang ditugaskan via WhatsApp
                  <br />
                  • Reminder berhenti jika tiket di-CLOSE atau status PENDING dengan catatan
                  <br />
                  • Tiket yang sudah mendapat reminder tidak akan diingatkan lagi dalam {reminderSettings.hours} jam ke depan
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
                </Button>
                
                <Button 
                  onClick={handleTestReminder}
                  variant="outline"
                  disabled={saving || !reminderSettings.enabled}
                >
                  Test Reminder Sekarang
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PPPoE Webhook Tab */}
        <TabsContent value="webhook">
          <div className="space-y-6">
            {/* Info Box */}
            <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0 dark:text-amber-400" />
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Apa itu PPPoE Webhook?</strong>
                    <p className="mt-1">
                      Webhook memungkinkan MikroTik mengirim notifikasi <strong>real-time</strong> ke sistem saat ada pelanggan connect/disconnect.
                      Ini lebih efisien daripada sistem polling yang mengecek status setiap beberapa menit.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scheduler Control - PENTING */}
            <Card className="border border-gray-200 dark:border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-5 h-5 text-red-500" />
                  Status Sync Scheduler (Polling)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-base font-medium">Aktifkan Polling Status</Label>
                    <p className="text-sm text-gray-500 dark:text-slate-400">
                      {webhookSettings.scheduler_enabled 
                        ? `Sistem mengecek status ke MikroTik setiap ${webhookSettings.scheduler_interval_minutes} menit`
                        : 'Sistem hanya menerima update dari webhook MikroTik (direkomendasikan)'
                      }
                    </p>
                  </div>
                  <Switch
                    checked={webhookSettings.scheduler_enabled}
                    onCheckedChange={(checked) => setWebhookSettings(prev => ({ ...prev, scheduler_enabled: checked }))}
                  />
                </div>
                
                {webhookSettings.scheduler_enabled && (
                  <div className="space-y-2">
                    <Label>Interval Polling (menit)</Label>
                    <Select
                      value={webhookSettings.scheduler_interval_minutes.toString()}
                      onValueChange={(value) => setWebhookSettings(prev => ({ 
                        ...prev, 
                        scheduler_interval_minutes: parseInt(value) 
                      }))}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 menit</SelectItem>
                        <SelectItem value="3">3 menit</SelectItem>
                        <SelectItem value="5">5 menit</SelectItem>
                        <SelectItem value="10">10 menit</SelectItem>
                        <SelectItem value="15">15 menit</SelectItem>
                        <SelectItem value="30">30 menit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {!webhookSettings.scheduler_enabled && (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      <span><strong>Mode Webhook Aktif</strong> - Hemat memory & real-time updates!</span>
                    </p>
                  </div>
                )}

                {webhookSettings.scheduler_enabled && (
                  <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-700 dark:text-orange-300 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      <span><strong>Mode Polling Aktif</strong> - Menggunakan lebih banyak memory. Pertimbangkan untuk nonaktifkan jika webhook sudah di-setup.</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* API Key Section */}
            <Card className="border border-gray-200 dark:border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Key className="w-5 h-5 text-amber-500" />
                  API Key untuk Webhook
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  API Key ini digunakan untuk mengamankan endpoint webhook dari akses tidak sah.
                </p>
                
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={webhookSettings.api_key}
                    readOnly
                    placeholder="Klik 'Generate' untuk membuat API Key"
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(webhookSettings.api_key, 'API Key')}
                    disabled={!webhookSettings.api_key}
                    title="Copy API Key"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>

                <Button onClick={generateApiKey} disabled={webhookLoading} variant="outline" className="gap-2">
                  <RefreshCw className={`w-4 h-4 ${webhookLoading ? 'animate-spin' : ''}`} />
                  Generate API Key Baru
                </Button>

                {webhookSettings.api_key && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠️ Jika generate API Key baru, update juga script di semua router MikroTik!
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Production URL */}
            <Card className="border border-gray-200 dark:border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-500" />
                  URL Production
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Masukkan URL production aplikasi Anda. URL ini akan digunakan di script MikroTik.
                </p>
                <Input
                  placeholder="https://billing.domain-anda.com"
                  value={webhookSettings.custom_webhook_url}
                  onChange={(e) => setWebhookSettings(prev => ({ ...prev, custom_webhook_url: e.target.value }))}
                />
                <p className="text-xs text-gray-400">
                  Kosongkan jika ingin menggunakan URL otomatis dari sistem.
                </p>
              </CardContent>
            </Card>

            {/* Push Webhook to All Routers */}
            <Card className="border border-blue-200 bg-blue-50 dark:bg-blue-900/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-blue-500" />
                  Push Webhook ke Semua Router
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Klik tombol di bawah untuk push/update script webhook ke <strong>semua router MikroTik</strong> yang terdaftar.
                  Gunakan fitur ini jika:
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 list-disc list-inside space-y-1">
                  <li>URL Production berubah</li>
                  <li>API Key di-generate ulang</li>
                  <li>Ada router yang belum ter-setup webhook</li>
                </ul>
                <Button 
                  onClick={pushWebhookToAllRouters} 
                  disabled={pushAllLoading || !webhookSettings.api_key}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                  data-testid="push-webhook-all-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${pushAllLoading ? 'animate-spin' : ''}`} />
                  {pushAllLoading ? 'Sedang Push...' : 'Push ke Semua Router'}
                </Button>
                {!webhookSettings.api_key && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠️ Generate API Key terlebih dahulu sebelum push webhook.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Push Hotspot Webhook to All Routers */}
            <Card className="border border-green-200 bg-green-50 dark:bg-green-900/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Wifi className="w-5 h-5 text-green-500" />
                  Push Hotspot Webhook ke Semua Router
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Klik tombol di bawah untuk push script webhook ke <strong>Hotspot User Profile</strong> di semua router.
                  Ini untuk tracking status voucher secara real-time.
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 list-disc list-inside space-y-1">
                  <li><strong>Login</strong> → Status voucher jadi "used"</li>
                  <li><strong>Logout/Expired</strong> → Status voucher jadi "expired"</li>
                </ul>
                <Button 
                  onClick={pushHotspotWebhookToAllRouters} 
                  disabled={pushHotspotLoading || !webhookSettings.api_key}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="push-hotspot-webhook-all-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${pushHotspotLoading ? 'animate-spin' : ''}`} />
                  {pushHotspotLoading ? 'Sedang Push...' : 'Push Hotspot Webhook'}
                </Button>
                {!webhookSettings.api_key && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠️ Generate API Key terlebih dahulu sebelum push webhook.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* MikroTik Script Template */}
            {webhookSettings.api_key && (
              <Card className="border border-gray-200 dark:border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Code className="w-5 h-5 text-green-500" />
                    Script MikroTik
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    Copy script ini dan paste ke PPPoE Profile di MikroTik Anda.
                  </p>
                  
                  {scriptTemplate ? (
                    <div className="space-y-4">
                      {/* Auto Install Script */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-green-600 font-medium dark:text-green-400">🚀 Auto Install Script (Semua Profile)</Label>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(scriptTemplate.on_up_script, 'Auto Install Script')}
                            className="gap-1 text-xs"
                          >
                            <Copy className="w-3 h-3" /> Copy
                          </Button>
                        </div>
                        <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto max-h-60">
                          {scriptTemplate.on_up_script}
                        </pre>
                      </div>

                      {/* Manual On-Down Script (Optional) */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-slate-500 font-medium dark:text-slate-400">📋 Script Manual (Opsional)</Label>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(scriptTemplate.on_down_script, 'Manual Script')}
                            className="gap-1 text-xs"
                          >
                            <Copy className="w-3 h-3" /> Copy
                          </Button>
                        </div>
                        <pre className="bg-gray-800 text-slate-400 p-4 rounded-lg text-xs overflow-x-auto max-h-32 dark:text-slate-500">
                          {scriptTemplate.on_down_script}
                        </pre>
                      </div>

                      {/* Setup Instructions */}
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-lg p-4">
                        <h4 className="font-medium text-green-800 dark:text-green-200 mb-2 flex items-center gap-2">
                          <Info className="w-4 h-4" /> Cara Pasang Webhook
                        </h4>
                        <ol className="text-sm text-green-700 dark:text-green-300 space-y-1 list-decimal list-inside">
                          <li>Copy script <strong>"Auto Install"</strong> di atas</li>
                          <li>Buka <strong>MikroTik Terminal</strong> (Winbox → New Terminal)</li>
                          <li>Paste script dan tekan <strong>Enter</strong></li>
                          <li>Script akan otomatis memasang webhook ke <strong>SEMUA PPP Profile</strong></li>
                          <li>Test dengan connect/disconnect salah satu pelanggan PPPoE</li>
                        </ol>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                      <Code className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Generate API Key terlebih dahulu untuk melihat script</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* RADIUS Accounting Sync Card */}
            <Card className="border-green-200 bg-green-50/30">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <CardTitle className="text-green-800">RADIUS Accounting Sync</CardTitle>
                </div>
                <p className="text-sm text-green-600 dark:text-green-400">
                  Sync status online/offline otomatis berdasarkan data RADIUS Accounting (tanpa webhook, tanpa memenuhi log MikroTik)
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border dark:bg-slate-800">
                  <div>
                    <Label className="font-medium">Aktifkan Auto Sync</Label>
                    <p className="text-sm text-gray-500 dark:text-slate-400">Sync status online/offline setiap beberapa menit</p>
                  </div>
                  <Switch
                    checked={radiusSyncSettings.enabled}
                    onCheckedChange={(checked) => setRadiusSyncSettings(prev => ({ ...prev, enabled: checked }))}
                  />
                </div>
                
                {radiusSyncSettings.enabled && (
                  <div className="p-4 bg-white rounded-lg border dark:bg-slate-800">
                    <Label className="font-medium mb-2 block">Interval Sync (menit)</Label>
                    <select
                      value={radiusSyncSettings.interval}
                      onChange={(e) => setRadiusSyncSettings(prev => ({ ...prev, interval: parseInt(e.target.value) }))}
                      className="w-full p-2 border rounded-lg"
                    >
                      <option value={1}>1 menit</option>
                      <option value={2}>2 menit</option>
                      <option value={5}>5 menit</option>
                      <option value={10}>10 menit</option>
                    </select>
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Button 
                    onClick={saveRadiusSyncSettings}
                    disabled={radiusSyncLoading}
                    className="bg-green-600 hover:bg-green-700 gap-2"
                  >
                    {radiusSyncLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    Simpan Pengaturan
                  </Button>
                  
                  <Button 
                    onClick={runManualSync}
                    disabled={manualSyncLoading}
                    variant="outline"
                    className="gap-2 border-green-600 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/30"
                  >
                    {manualSyncLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Sync Manual Sekarang
                  </Button>
                </div>
                
                <div className="bg-green-100 p-3 rounded-lg text-sm text-green-700 dark:bg-green-900/40 dark:text-green-300">
                  <strong>Keuntungan RADIUS Accounting Sync:</strong>
                  <ul className="list-disc list-inside mt-1">
                    <li>Tidak memerlukan webhook script di MikroTik</li>
                    <li>Tidak memenuhi log MikroTik</li>
                    <li>Tidak menyebabkan "maximum connection count" error</li>
                    <li>Data diambil langsung dari tabel radacct</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button 
                onClick={saveWebhookSettings} 
                disabled={webhookLoading}
                className="bg-indigo-600 hover:bg-indigo-700 gap-2"
              >
                {webhookLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Simpan Pengaturan Webhook
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Portal Pelanggan Tab */}
        <TabsContent value="portal">
          <div className="space-y-6">
            {/* Portal Link Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-indigo-500" />
                  <CardTitle>Link Portal Pelanggan</CardTitle>
                </div>
                <CardDescription>
                  Bagikan link ini kepada pelanggan untuk akses portal self-service
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {tenantSlug ? (
                  <>
                    {/* Portal Login Link */}
                    <div className="space-y-3">
                      <Label className="text-base font-medium">Link Login Portal</Label>
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        Link langsung ke halaman login portal pelanggan
                      </p>
                      <div className="flex gap-2">
                        <Input 
                          value={portalLoginUrl} 
                          readOnly 
                          className="bg-gray-50 font-mono text-sm dark:bg-slate-900"
                        />
                        <Button
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(portalLoginUrl);
                            setPortalLinkCopied(true);
                            toast.success('Link berhasil disalin!');
                            setTimeout(() => setPortalLinkCopied(false), 2000);
                          }}
                          className="shrink-0 gap-2"
                        >
                          {portalLinkCopied ? <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" /> : <Copy className="w-4 h-4" />}
                          Salin
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => window.open(portalLoginUrl, '_blank')}
                          className="shrink-0 gap-2"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Buka
                        </Button>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200 dark:border-slate-700"></div>

                    {/* Portal Info Page (with QR) */}
                    <div className="space-y-3">
                      <Label className="text-base font-medium flex items-center gap-2">
                        <QrCode className="w-4 h-4 text-indigo-500" />
                        Halaman Info Portal (dengan QR Code)
                      </Label>
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        Halaman informatif dengan QR code yang bisa dicetak dan dibagikan ke pelanggan
                      </p>
                      <div className="flex gap-2">
                        <Input 
                          value={portalInfoUrl} 
                          readOnly 
                          className="bg-gray-50 font-mono text-sm dark:bg-slate-900"
                        />
                        <Button
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(portalInfoUrl);
                            toast.success('Link berhasil disalin!');
                          }}
                          className="shrink-0 gap-2"
                        >
                          <Copy className="w-4 h-4" />
                          Salin
                        </Button>
                        <Button
                          onClick={() => window.open(portalInfoUrl, '_blank')}
                          className="shrink-0 gap-2 bg-indigo-600 hover:bg-indigo-700"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Buka
                        </Button>
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 rounded-lg p-4">
                      <h4 className="font-medium text-indigo-800 dark:text-indigo-200 mb-2 flex items-center gap-2">
                        <Smartphone className="w-4 h-4" /> Fitur Portal Pelanggan
                      </h4>
                      <ul className="text-sm text-indigo-700 dark:text-indigo-300 space-y-1 list-disc list-inside">
                        <li><strong>Bayar Tagihan</strong> - Pembayaran via transfer bank</li>
                        <li><strong>Lihat Invoice</strong> - Download dan cetak invoice PDF</li>
                        <li><strong>Kelola WiFi</strong> - Ubah nama dan password WiFi (jika ONT terhubung)</li>
                        <li><strong>Ubah Password</strong> - Ganti password akun portal</li>
                      </ul>
                    </div>

                    {/* Tips */}
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-4">
                      <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                        <Info className="w-4 h-4" /> Tips Berbagi Link
                      </h4>
                      <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1 list-disc list-inside">
                        <li>Buka <strong>Halaman Info Portal</strong> untuk mendapatkan QR Code</li>
                        <li>Cetak halaman tersebut dan tempel di kantor atau booth</li>
                        <li>Kirim link via WhatsApp kepada pelanggan</li>
                        <li>Password default pelanggan: <code className="bg-amber-200 px-1 rounded">12345678</code></li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Share2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 dark:text-slate-400">Portal pelanggan tidak tersedia.</p>
                    <p className="text-gray-400 text-sm mt-1">Hubungi administrator untuk mengaktifkan fitur ini.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Billing Tab */}
        {/* Billing Tab */}
        <TabsContent value="billing" className="mt-0">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-indigo-500" />
                <CardTitle>Metode Perpanjangan Langganan</CardTitle>
              </div>
              <CardDescription>
                Menentukan bagaimana tanggal jatuh tempo baru dihitung saat pelanggan melakukan pembayaran
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Metode Perpanjangan</Label>
                <Select
                  value={billingSettings.extend_method}
                  onValueChange={(v) => setBillingSettings(prev => ({ ...prev, extend_method: v }))}
                >
                  <SelectTrigger data-testid="select-extend-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="from_due_date">Dari Tanggal Jatuh Tempo (jatuh tempo + 1 bulan)</SelectItem>
                    <SelectItem value="from_payment_date">Dari Tanggal Bayar (tanggal bayar + 1 bulan)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <Info className="w-4 h-4 inline mr-1" />
                  <strong>Dari Tanggal Jatuh Tempo</strong>: pelanggan yang bayar lebih awal tidak kehilangan sisa masa aktif (Recommended).
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-indigo-500" />
                <CardTitle>Penerbitan Tagihan Sebelum Jatuh Tempo (H-N)</CardTitle>
              </div>
              <CardDescription>
                Tagihan periode berikutnya otomatis diterbitkan dan bisa dibayar mulai <strong>N hari</strong> sebelum tanggal jatuh tempo (pola aplikasi lama). Contoh: isi <strong>5</strong> artinya tagihan baru muncul mulai H-5 sebelum jatuh tempo.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700 dark:text-slate-300">H-minus</span>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  className="w-28"
                  data-testid="input-pregen-days"
                  value={billingSettings.invoice_pregen_days ?? 5}
                  onChange={(e) => setBillingSettings(prev => ({ ...prev, invoice_pregen_days: parseInt(e.target.value || '5', 10) }))}
                />
                <span className="text-sm text-gray-500 dark:text-slate-400">hari sebelum jatuh tempo</span>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-indigo-500" />
                <CardTitle>Tanggal Jatuh Tempo Pascabayar</CardTitle>
              </div>
              <CardDescription>
                Acuan tanggal saat pelanggan pascabayar diperpanjang / dilunasi
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select
                value={billingSettings.postpaid_due_anchor}
                onValueChange={(v) => setBillingSettings(prev => ({ ...prev, postpaid_due_anchor: v }))}
              >
                <SelectTrigger data-testid="select-postpaid-due-anchor"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cycle_day" data-testid="postpaid-anchor-cycle-day">Tanggal tetap sesuai hari jatuh tempo pelanggan (mis. selalu tgl 5 / 10)</SelectItem>
                  <SelectItem value="invoice_due" data-testid="postpaid-anchor-invoice-due">Lanjut dari tanggal jatuh tempo tagihan (21/10 jadi 21/11)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Pilih "Lanjut dari tanggal jatuh tempo tagihan" bila tanggal jatuh tempo tiap pelanggan berbeda-beda mengikuti tanggal pasang.
              </p>
            </CardContent>
          </Card>

          <div className="flex justify-end mt-4">
            <Button onClick={saveBillingSettings} disabled={savingBilling} data-testid="save-billing-settings-btn" className="gap-2">
              {savingBilling ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {savingBilling ? 'Menyimpan...' : 'Simpan Pengaturan'}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
